import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Box
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import { useTranslation } from 'react-i18next';

interface ImageItem {
  id: string;
  name: string;
  url: string;
  folderId: string;
  createdAt: string;
  createdBy: string;
  size?: number;
  type?: string;
}

interface ImageDeleteModalProps {
  open: boolean;
  onClose: () => void;
  image: ImageItem | null;
  onDelete: (imageId: string) => Promise<void>;
}

const ImageDeleteModal: React.FC<ImageDeleteModalProps> = ({
  open,
  onClose,
  image,
  onDelete
}) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Modal kapanırken temizle
  const handleClose = () => {
    if (isDeleting) return;
    setError(null);
    onClose();
  };

  // Resmi sil
  const handleDelete = async () => {
    if (!image) return;

    setIsDeleting(true);
    setError(null);

    try {
      await onDelete(image.id);
      
      // State güncellemesinin tamamlanması için kısa bir gecikme
      await new Promise(resolve => setTimeout(resolve, 100));
      
      handleClose();
    } catch (error: any) {
      console.error('Resim silme hatası:', error);
      // Backend'den gelen hata mesajını göster
      const errorMessage = error?.message || tt('Resim silinirken bir hata oluştu', 'An error occurred while deleting image');
      setError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  // Dosya boyutunu formatla
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!image) return null;

  return (
    <Dialog
      open={open}
      onClose={!isDeleting ? handleClose : undefined}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle 
        sx={{ 
          display: 'flex', 
          alignItems: 'center',
          color: 'error.main'
        }}
      >
        <WarningIcon sx={{ mr: 1 }} />
        {tt('Resim Silme Onayı', 'Image Delete Confirmation')}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" gutterBottom>
            {tt('Aşağıdaki resmi silmek istediğinizden emin misiniz?', 'Are you sure you want to delete the following image?')}
          </Typography>
          
          <Box 
            sx={{ 
              p: 2, 
              bgcolor: 'grey.100', 
              borderRadius: 1, 
              border: '1px solid',
              borderColor: 'grey.300',
              my: 2,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Box
              component="img"
              src={image.url}
              alt={image.name}
              sx={{
                width: 80,
                height: 80,
                objectFit: 'cover',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.300',
                mr: 2
              }}
            />
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                🖼️ {image.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {image.size ? formatFileSize(image.size) : tt('Bilinmiyor', 'Unknown')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {new Date(image.createdAt).toLocaleDateString(localeCode)}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>{tt('Not:', 'Note:')}</strong> {tt('Resim pasif duruma getirilecektir. Fiziksel dosya silinmeyecektir.', 'The image will be marked passive. The physical file will not be deleted.')}
          </Typography>
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={handleClose} 
          color="inherit"
          disabled={isDeleting}
        >
          {tt('İptal', 'Cancel')}
        </Button>
        <Button 
          onClick={handleDelete} 
          variant="contained"
          color="error"
          disabled={isDeleting}
          startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
        >
          {isDeleting ? tt('Siliniyor...', 'Deleting...') : tt('Evet, Sil', 'Yes, Delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageDeleteModal; 